// The deck way material (client/src/deckway.js: the pieces, the module
// header): the ground's way code (client/src/groundmat.js groundDecl) driven
// per vertex — aDw = (o across, s along, the look byte, the run width), the
// paint parts aPart / aLane / aCut — lit by the bake's light model at
// visibility 1, then three's tone mapping (the one `look` curve).
import * as THREE from 'three';
import { MARKS, PAINT } from '../../game/waysdraw.js';
import WAYSLOOK from '../../pipeline/wayslook.json';
import { bindGround, groundDecl, groundKey, glslMin } from './groundmat.js';
import { detailTier } from './terrain.js';
import { PATH_KINDS } from './deckway.js';

const KN = WAYSLOOK.kinds;
const f4 = (x) => Number(x).toFixed(4);
const WB = WAYSLOOK.ballast;
// the albedo of a kind the bake has none for (uWayAlb 0, and no PATH-G mean
// tread GM_PATHBK): a mid grey, never black
const ALB_NONE = 0.12;

// the deck way block (in place of map_fragment): the colour of the way at
// the fragment x its ground detail x the bake's light. q = the ground tier
// (the fades / weights / clamp: groundmat's uniforms uGmFade[q], uGmMat,
// uGmMat2 — STABLE TEXT, no renderconfig number in the program)
function deckWayGLSL(q) {
  const isPath = (v) => PATH_KINDS.map((k) => `${v} == ${k}`).join(' || ');
  return `
  {
    vec3 gP = vDwP;
    vec2 gdx = dFdx(gP.xz), gdy = dFdy(gP.xz);
    float gFW = max(max(length(gdx), length(gdy)), 0.004);
    vec3 Ng = normalize(cross(dFdx(gP), dFdy(gP)));
    if (Ng.y < 0.0) Ng = -Ng;
    float o = vDw.x, s = vDw.y, h = 0.5 * max(vDw.w, 0.1);
    int look = int(vDw.z + 0.5);
    int kind = look & 15, hi = look >> 4;
    bool pk = ${isPath('kind')};
    // the side strip (a built kind's sidewalk / ring apron beyond the
    // carriageway bound, the paint parts in metres across)
    float c0 = vPart.y * 2.0 * h - h, c1 = vPart.z * 2.0 * h - h;
    float s0 = vPart.x * 2.0 * h - h, s1 = vPart.w * 2.0 * h - h;
    float dS = max(c0 - o, o - c1);
    int sk = (o < s0 || o > s1) ? hi : ${KN[WAYSLOOK.apron]};
    float sOk = (!pk && sk > 0 && (c0 > -h + 1e-3 || c1 < h - 1e-3)) ? 1.0 : 0.0;
    float ws = sOk * clamp(0.5 + dS / gFW, 0.0, 1.0);
    vec3 A = mix(dwAlb(kind), dwAlb(sk), ws);
    A = mix(A, GM_WKERB, sOk * gmCov(dS, 0.0, GM_WKERB_M, gFW));
    // lane lines (aLane: lanes against + 64 when the centre line is solid,
    // lanes with; hidden over aCut), dashed in metres along the run
    if (!pk && dS < 0.0 && mod(vLane.x, 64.0) + vLane.y > 1.5 && !(s >= vCut.x && s <= vCut.y)) {
      float sol = step(63.5, vLane.x), lb = vLane.x - 64.0 * sol;
      float n = lb + vLane.y, om = o - c0, lane = (c1 - c0) / n;
      float kr = floor(om / lane + 0.5);
      if (kr >= 1.0 && kr <= n - 1.0) {
        bool ctr = abs(kr - lb) < 0.5 && lb > 0.5 && vLane.y > 0.5;
        vec2 dg = ctr ? vec2(${f4(MARKS.centre_dash_m)}, ${f4(MARKS.centre_gap_m)}) : vec2(${f4(MARKS.lane_dash_m)}, ${f4(MARKS.lane_gap_m)});
        float x = mod(s + 0.5 * dg.y, dg.x + dg.y) - 0.5 * dg.y;
        float on = ctr && sol > 0.5 ? 1.0 : clamp(0.5 + min(x, dg.x - x) / gFW, 0.0, 1.0);
        A = mix(A, GM_WLINE, gmCov(om - kr * lane, -0.5 * GM_WLINE_M, 0.5 * GM_WLINE_M, gFW) * on);
      }
    }
    // rail sleepers on the field's phase (s = k x pitch), band-limited
    if (kind == ${KN.ballast}) {
      const float pitch = ${f4(WB.sleeper_pitch_m)};
      float x = (s / pitch - floor(s / pitch + 0.5)) * pitch;
      float ks = gmKeep(pitch, gFW);
      float cs = (ks * gmCov(x, ${f4(-WB.sleeper_w_m / 2)}, ${f4(WB.sleeper_w_m / 2)}, gFW) + (1.0 - ks) * ${f4(WB.sleeper_w_m / WB.sleeper_pitch_m)})
               * gmCov(o, ${f4(-WB.sleeper_len_m / 2)}, ${f4(WB.sleeper_len_m / 2)}, gFW);
      A = mix(A, GM_WSLEEPER, cs);
    }
    // the kind's layer (+ a track's rut layer / the side strip's), hex-tiled
    // on the world xz like the ground, height-blended; its detail normal and
    // cavity in the relight — the ground's fades
    float kN = (1.0 - smoothstep(uGmFade[${q}].x, uGmFade[${q}].y, vDwD)) * uMatOn;
    float kR = (1.0 - smoothstep(uGmFade[${q}].z, uGmFade[${q}].w, vDwD)) * uMatOn;
    vec3 gD = vec3(1.0), Nd = Ng;
    float cav = 1.0;
    if (kN > 0.001 || kR > 0.001) {
      bool nbk = kR > 0.001;
      int m1 = GM_PL[kind], m2 = -1;
      float wb = 0.0, nk = 1.0;
      if (pk) { float r; gmPathCross(kind, hi, min(abs(o) / h, 1.0), nk, r); m2 = GM_P2[kind]; wb = r; }
      else if (sOk > 0.5) { m2 = GM_PL[sk]; wb = ws; }
      if (m2 == m1 || m2 < 0) { m2 = -1; wb = 0.0; }
      GmS S1 = gmNeutral(), S2 = gmNeutral();
      float i1 = GM_INVM[clamp(m1, 0, GM_NL - 1)], i2 = GM_INVM[clamp(m2, 0, GM_NL - 1)];
      if (m1 >= 0) S1 = gmHex(m1, gP.xz * i1, gdx * i1, gdy * i1, nbk);
      if (m2 >= 0 && wb >= 0.001) S2 = gmHex(m2, gP.xz * i2, gdx * i2, gdy * i2, nbk);
      float h1 = S1.a.a + (1.0 - wb), h2 = S2.a.a + wb;
      float hm = max(h1, h2) - uGmMat2.z;
      float b1 = max(h1 - hm, 0.0), b2 = max(h2 - hm, 0.0);
      float wh = wb < 0.001 ? 0.0 : b2 / max(b1 + b2, 1e-5);
      gD = mix(vec3(1.0), mix(S1.a.rgb, S2.a.rgb, wh) * 2.0, kN * uGmMat.z);
      cav = mix(1.0, mix(S1.c, S2.c, wh) * 2.0, kR);
      if (nbk) Nd = gmPerturb(Ng, vec3(1.0, 0.0, 0.0), vec3(0.0, 0.0, 1.0), mix(S1.n, S2.n, wh) * (uGmMat.w * nk));
    }
    // the bake's light model, sky and sun visibility 1; the relight ratio
    // clamped as the ground's
    vec3 E0 = gmE(Ng, 1.0, 1.0);
    vec3 gF = mix(vec3(1.0), clamp(gmE(Nd, 1.0, cav) / max(E0, vec3(1e-4)), uGmMat2.x, uGmMat2.y), kR);
    diffuseColor.rgb *= A * gD * E0 * gF;
  }`;
}

const DECL = `
varying vec4 vDw;
varying vec4 vPart;
varying vec2 vLane;
flat varying vec2 vCut;
varying vec3 vDwP;
varying float vDwD;
// a kind's albedo: the painter's mean (uWayAlb), a PATH-G kind's mean tread
vec3 dwAlb(int k) {
  vec3 c = uWayAlb[k];
  if (dot(c, vec3(1.0)) < 1e-4) c = GM_PATHBK[k];
  if (dot(c, vec3(1.0)) < 1e-4) c = vec3(${f4(ALB_NONE)});
  return c;
}
`;

// ONE material for every deck way (the kind rides the vertices): unlit
// MeshBasic like the terrain (the light is computed here), fog, tone-mapped;
// DoubleSide + the polygon offset of the old deck ribbons (a strip's winding
// flips at a hairpin; 0.01 m over the structure top)
export function deckWayMaterial() {
  const m = new THREE.MeshBasicMaterial({ color: 0xffffff, fog: true, polygonOffset: true, polygonOffsetFactor: -2, polygonOffsetUnits: -2,
    side: THREE.DoubleSide });
  m.onBeforeCompile = (sh) => {
    const q = detailTier();
    bindGround(sh.uniforms);
    sh.vertexShader = sh.vertexShader
      .replace('#include <common>', '#include <common>\nattribute vec4 aDw;\nattribute vec4 aPart;\nattribute vec2 aLane;\nattribute vec2 aCut;\n'
        + 'varying vec4 vDw;\nvarying vec4 vPart;\nvarying vec2 vLane;\nflat varying vec2 vCut;\nvarying vec3 vDwP;\nvarying float vDwD;')
      .replace('#include <fog_vertex>', '#include <fog_vertex>\nvDw = aDw;\nvPart = aPart;\nvLane = aLane;\nvCut = aCut;\n'
        + 'vDwP = (modelMatrix * vec4(transformed, 1.0)).xyz;\nvDwD = -mvPosition.z;');
    sh.fragmentShader = sh.fragmentShader
      .replace('#include <common>', '#include <common>\n' + groundDecl(q, false, { use: 'deck' }) + DECL)
      .replace('#include <map_fragment>', deckWayGLSL(q));
    sh.vertexShader = glslMin(sh.vertexShader);
    sh.fragmentShader = glslMin(sh.fragmentShader);
  };
  m.customProgramCacheKey = () => `deckway-q${detailTier()}-${groundKey()}-${f4(PAINT.kerb_line_m)}`;
  return m;
}
